import type {
  MappedOrganisationScale,
  OrganisationScale,
  ScaleKey,
} from "@/types";
import { SCALE_KEYS } from "@/types";

export const getScaleValue = (scale: ScaleKey): number | null => {
  const index = SCALE_KEYS.indexOf(scale);
  return index !== -1 ? index : null;
};

export const getScaleFromValue = (value: number): ScaleKey | null => {
  return SCALE_KEYS[value] || null;
};

export const formatLabel = (
  scale: MappedOrganisationScale,
  t: (key: string) => string,
  type: string
): string => {
  const baseLabel = t(`organisation.settings.scale.${scale.scale}`);

  if (type) {
    const descriptionKey =
      `description_${type}` as keyof MappedOrganisationScale;
    if (scale && Object.prototype.hasOwnProperty.call(scale, descriptionKey)) {
      return `${baseLabel} | ${scale[descriptionKey]}`;
    } else {
      return baseLabel;
    }
  }

  return baseLabel;
};

export const getProbability = (
  scales: ReadonlyArray<MappedOrganisationScale>,
  index: number
): number | null => {
  if (index === 0) return 0;
  const currentScale = scales[index];

  const nextScale = scales[index + 1];
  if (currentScale && nextScale) {
    return (currentScale.probability_min + nextScale?.probability_min) / 2;
  } else {
    return (currentScale.probability_min + 1) / 2;
  }
};

/**
 * Returns a numeric financial value based on scale index.
 * - If there's a "next" scale, compute midpoint of current & next .financial_min
 * - If this is the largest scale, return currentScale.financial_min * 1.5
 */
export const getFinancialValue = (
  scales: ReadonlyArray<MappedOrganisationScale>,
  index: number
): number => {
  if (index === 0) {
    return 0;
  }

  const currentScale = scales[index];
  const nextScale = scales[index + 1];

  if (currentScale && nextScale) {
    return nextScale.financial_min;
  } else if (currentScale) {
    // If at largest scale (no nextScale),
    // take currentScale.financial_min * 1.5
    return currentScale.financial_min * 1.5;
  }

  return 0;
};

export const getScaleIndexByProbabilityRange = (
  scales: ReadonlyArray<MappedOrganisationScale>,
  probability: number | null | undefined
): number => {
  // If probability is null, undefined, or can't be parsed, return "n/a" (index 0)
  if (
    probability === null ||
    probability === undefined ||
    isNaN(Number.parseFloat(probability as unknown as string))
  ) {
    return 0;
  }

  // If probability is 0, return "n/a" (index 0)
  if (Number.parseFloat(probability as unknown as string) === 0) {
    return 0;
  }

  // Start from index 1 to skip "n/a"
  for (let i = 1; i < scales.length; i++) {
    const currentScale = scales[i];
    const nextScale = scales[i + 1];

    // Set the upper boundary to the next scale’s probability_min, or 1 if this is the last scale
    const upperLimit = nextScale ? nextScale?.probability_min : 1;

    // Check if the probability falls within the current scale's range
    if (
      probability > currentScale.probability_min &&
      probability <= upperLimit
    ) {
      return i;
    }
  }

  // Return -1 if no scale fits (for edge cases, but ideally shouldn't happen)
  return scales.length - 1;
};

export const getScaleIndexByFinancialRange = (
  scales: ReadonlyArray<MappedOrganisationScale>,
  financial: number | null | undefined
): number => {
  // If financial value is null, undefined, or can't be parsed, return "n/a" (index 0)
  if (
    financial === null ||
    financial === undefined ||
    financial === 0 ||
    isNaN(Number.parseFloat(financial as unknown as string))
  ) {
    return 0;
  }

  // Start from index 1 to skip "n/a"
  for (let i = 1; i < scales.length; i++) {
    const currentScale = scales[i];
    const nextScale = scales[i + 1];

    // Set the upper boundary to the next scale's financial_min, or a large number if this is the last scale
    const upperLimit = nextScale ? nextScale.financial_min : Number.MAX_VALUE;

    // Check if the financial value falls within the current scale's range
    if (financial > currentScale.financial_min && financial <= upperLimit) {
      return i;
    }
  }

  // Return -1 if no scale fits (for edge cases)
  return scales.length - 1;
};

export const getScaleByFinancialRange = (
  scales: ReadonlyArray<MappedOrganisationScale>,
  financial: number
): ScaleKey => SCALE_KEYS[getScaleIndexByFinancialRange(scales, financial)];
export const getScaleByProbabilityRange = (
  scales: ReadonlyArray<MappedOrganisationScale>,
  financial: number
): ScaleKey => SCALE_KEYS[getScaleIndexByProbabilityRange(scales, financial)];

export const getIndexByScale = (
  scales: ReadonlyArray<MappedOrganisationScale>,
  scaleValue: ScaleKey
): number => {
  return scales.findIndex((scale) => scale.scale === scaleValue);
};
export const getGridCoordinateFinancial = (
  scales: ReadonlyArray<MappedOrganisationScale>,
  probability: number,
  financial: number
): { x: number; y: number } => {
  // Get the indices for the probability and financial range
  const probabilityIndex = getScaleIndexByProbabilityRange(
    scales,
    probability || 0
  );
  const financialIndex = getScaleIndexByFinancialRange(scales, financial);

  // Return as a coordinate object
  return { x: probabilityIndex, y: financialIndex };
};

export const getGridCoordinateScale = (
  scales: ReadonlyArray<MappedOrganisationScale>,
  probability: number,
  scale: ScaleKey
): { x: number; y: number } => {
  // Get the indices for the probability and financial range
  const probabilityIndex = getScaleIndexByProbabilityRange(scales, probability);
  const financialIndex = getIndexByScale(scales, scale);

  // Return as a coordinate object
  return { x: probabilityIndex, y: financialIndex };
};

export const mapOrganisationScales = (
  organisationScales: OrganisationScale
): ReadonlyArray<MappedOrganisationScale> => {
  return SCALE_KEYS.map((scale) => {
    // Convert "n/a" to "n_a" for database field name compatibility
    const dbFieldName = scale === "n/a" ? "n_a" : scale;

    return {
      scale,
      description_probability: String(
        organisationScales[`${dbFieldName}_scale_description_probability`] ?? ""
      ),
      description_financial: String(
        organisationScales[`${dbFieldName}_scale_description_financial`] ?? ""
      ),
      description_image: String(
        organisationScales[`${dbFieldName}_scale_description_image`] ?? ""
      ),
      description_time: String(
        organisationScales[`${dbFieldName}_scale_description_time`] ?? ""
      ),
      description_environment: String(
        organisationScales[`${dbFieldName}_scale_description_environment`] ?? ""
      ),
      description_safety: String(
        organisationScales[`${dbFieldName}_scale_description_safety`] ?? ""
      ),
      description_objective: String(
        organisationScales[`${dbFieldName}_scale_description_objective`] ?? ""
      ),
      probability_min:
        Number(organisationScales[`${dbFieldName}_scale_probability_min`]) || 0,
      financial_min:
        Number(organisationScales[`${dbFieldName}_scale_financial_min`]) || 0,
    };
  });
};

export const mapScalesForSubmission = (
  mappedOrganisationScales: ReadonlyArray<MappedOrganisationScale>
): OrganisationScale => {
  const scalesData = mappedOrganisationScales.reduce((acc, scale) => {
    // Convert "n/a" to "n_a" for database field name compatibility
    const dbScaleName = scale.scale === "n/a" ? "n_a" : scale.scale;
    const keyPrefix = `${dbScaleName}_scale`;

    acc[`${keyPrefix}_description_probability`] = scale.description_probability;
    acc[`${keyPrefix}_description_financial`] = scale.description_financial;
    acc[`${keyPrefix}_description_image`] = scale.description_image;
    acc[`${keyPrefix}_description_time`] = scale.description_time;
    acc[`${keyPrefix}_description_environment`] = scale.description_environment;
    acc[`${keyPrefix}_description_safety`] = scale.description_safety;
    acc[`${keyPrefix}_description_objective`] = scale.description_objective;
    acc[`${keyPrefix}_probability_min`] = scale.probability_min;
    acc[`${keyPrefix}_financial_min`] = scale.financial_min;

    return acc;
  }, {} as Record<string, unknown>);

  // Delete the financial_min and probability_min for n/a and very_small scales
  delete scalesData.n_a_scale_financial_min;
  delete scalesData.n_a_scale_probability_min;
  delete scalesData.very_small_scale_financial_min;
  delete scalesData.very_small_scale_probability_min;

  return scalesData as unknown as OrganisationScale;
};
